import { useState } from 'react';

import { Button } from '../../shared/ui/button';
import { ConfirmDelete } from '../../shared/ui/confirm-delete';
import { MutationError } from '../../shared/ui/mutation-error';
import { EmptyState, ErrorState, LoadingState } from '../../shared/ui/states';
import type { TBookmark } from '../../entities/bookmark';
import { BookmarkHeader, BookmarkRow } from './bookmark-row';
import { useSetDefaultBookmark } from './use-bookmark-sample';
import { useBookmarks } from './use-bookmarks';

interface IBookmarksPanelProps {
  editable: boolean;
  timeZone?: string;
}

export const BookmarksPanel = ({ editable, timeZone }: IBookmarksPanelProps) => {
  const { list, remove } = useBookmarks();
  const useIn = useSetDefaultBookmark();
  const [removing, setRemoving] = useState<TBookmark | null>(null);
  const now = Date.now();

  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  if (list.isPending) return <LoadingState rows={6} />;

  const rows = list.data.pages.flatMap((page) => page.data);

  if (rows.length === 0) {
    return (
      <EmptyState
        title="No bookmarks yet"
        description="Open a request in Events and keep it under a name — it will show up here, and first in “Test with” for flows that start from its source."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <MutationError error={useIn.error} />

      <div role="table" aria-label="Bookmarks" className="border-b border-border-row">
        <BookmarkHeader />
        {rows.map((bookmark) => (
          <BookmarkRow
            key={bookmark.id}
            bookmark={bookmark}
            editable={editable}
            now={now}
            timeZone={timeZone}
            onUseIn={(flowId) => useIn.mutate({ flowId, bookmarkId: bookmark.id })}
            onRemove={() => setRemoving(bookmark)}
          />
        ))}
      </div>

      {list.hasNextPage && (
        <Button
          variant="secondary"
          size="sm"
          className="self-start"
          waiting={list.isFetchingNextPage}
          waitingLabel="Loading"
          onClick={() => void list.fetchNextPage()}
        >
          Show more
        </Button>
      )}

      <ConfirmDelete
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(null);
            remove.reset();
          }
        }}
        entity="bookmark"
        verb="Remove"
        name={removing?.name ?? ''}
        consequences="The request it kept is no longer kept past retention, unless another bookmark keeps it."
        note={<MutationError error={remove.error} />}
        waiting={remove.isPending}
        onConfirm={() => {
          if (removing) remove.mutate(removing.id, { onSuccess: () => setRemoving(null) });
        }}
      />
    </div>
  );
};
